/**
 * Subdomain-per-tenant: hostname에서 tenantId 추출
 *
 * @see doc/system-completion-plan/01-target/TENANT_HOST_PATH_PLANES.md
 * @see doc/system-completion-plan/04-gaps/SUBDOMAIN_PER_TENANT.md
 */

/**
 * 테넌트 slug로 사용할 수 없는 예약 서브도메인.
 * www.inflomatrix.app → 플랫폼 루트로 처리 (테넌트 아님).
 * 테넌트로 등록하려면 www.www.inflomatrix.app 형태가 되어야 하므로 현실적으로 불필요.
 */
export const RESERVED_SUBDOMAINS = ['www', 'app', 'api', 'platform', 'mail', 'cdn'] as const;

/** hostname에서 포트를 제거하고 소문자로 정규화한다. */
export function normalizeHostname(hostname: string): string {
  return (hostname.split(':')[0] ?? hostname).toLowerCase();
}

/**
 * 현재 hostname과 루트 도메인으로부터 tenantId를 추출한다.
 * @param hostname - 예: window.location.hostname
 * @param rootDomain - 예: inflomatrix.app (env: VITE_TENANT_ROOT_DOMAIN). 빈 값이면 서브도메인 해석 안 함.
 * @returns tenant slug 또는 null (플랫폼 루트/예약 서브도메인/로컬)
 */
export function getTenantIdFromHost(
  hostname: string,
  rootDomain: string | undefined
): string | null {
  const host = normalizeHostname(hostname);

  /** Playwright·Vite 로컬: `{tenant}.localhost` (production root와 무관) */
  if (host.endsWith('.localhost')) {
    const sub = host.slice(0, -'.localhost'.length);
    return sub || null;
  }

  const root = (rootDomain || '').trim().toLowerCase();
  if (!root) return null;

  if (host === root) return null;
  if (host.endsWith('.' + root)) {
    const sub = host.slice(0, -root.length - 1);
    if (!sub) return null;
    // www.inflomatrix.app 등 예약 서브도메인은 플랫폼 루트로 취급
    if ((RESERVED_SUBDOMAINS as readonly string[]).includes(sub)) return null;
    return sub;
  }
  return null;
}
